class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    // Hide visually but keep layout to prevent height collapse (no CLS)
    oldNode.style.visibility = 'hidden';
    oldNode.style.pointerEvents = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {

      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        this.productVariantSelects = this.querySelector('product-variant-selects');

        // Variant image map properties
        this.variantImageMap = this.parseVariantImageMap();
        this.currentVariantImageKey = null;
        // Separate cache for slider and grid since both exist
        this.originalSliderHTML = null;
        this.originalGridHTML = null;
        this.originalThumbsHTML = null;
        this.galleryLoader = this.productMediaGallery?.querySelector('[data-media-loader]');
        this.hasInitializedVariantGallery = false;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        this.initUpdateMedia();

        // Cache original gallery state BEFORE any mapping is applied
        this.cacheOriginalGalleryState();

        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
          this.updateVariantMedia();
        }

        // Apply variant image map on initial load
        const initialVariant = this.getSelectedVariant(this);
        this.applyVariantImageMap(initialVariant);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push(newNode => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        if (!this.contains(event.target)) return;

        // Capture scroll position at the start of variant change
        const savedScrollY = window.scrollY;
        const savedScrollX = window.scrollX;

        // Restore scroll after DOM updates
        const restoreScroll = () => {
          window.scrollTo({ left: savedScrollX, top: savedScrollY, behavior: 'instant' });
        };

        // Schedule multiple restore attempts
        requestAnimationFrame(restoreScroll);
        setTimeout(restoreScroll, 50);
        setTimeout(restoreScroll, 100);
        setTimeout(restoreScroll, 200);

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return html => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // Focus removed to prevent page scrolling on PDP
          })
          .catch(error => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const currentVariantSelects = this.variantSelectors;
        const incomingVariantSelects = html.querySelector("product-variant-selects");

        if (!currentVariantSelects || !incomingVariantSelects) {
          return;
        }

        this.syncVariantSelectors(currentVariantSelects, incomingVariantSelects);
        this.productVariantSelects = currentVariantSelects;
      }

      syncVariantSelectors(currentVariantSelects, incomingVariantSelects) {
        this.updateVariantSelectorScripts(currentVariantSelects, incomingVariantSelects);
        this.syncVariantOptionGroups(currentVariantSelects, incomingVariantSelects);

        Array.from(incomingVariantSelects.attributes).forEach(attribute => {
          if (attribute.name.startsWith('data-')) {
            currentVariantSelects.setAttribute(attribute.name, attribute.value);
          }
        });
      }

      updateVariantSelectorScripts(currentVariantSelects, incomingVariantSelects) {
        ['[data-selected-variant]', '[data-all-variants]', '[data-selected-variant-options-handle]'].forEach(selector => {
          const currentScript = currentVariantSelects.querySelector(selector);
          const incomingScript = incomingVariantSelects.querySelector(selector);

          if (currentScript && incomingScript) {
            currentScript.textContent = incomingScript.textContent;
          }
        });
      }

      syncVariantOptionGroups(currentVariantSelects, incomingVariantSelects) {
        const currentGroups = Array.from(currentVariantSelects.querySelectorAll('[data-option-name]'));
        const incomingGroups = Array.from(incomingVariantSelects.querySelectorAll('[data-option-name]'));

        incomingGroups.forEach(incomingGroup => {
          const optionName = incomingGroup.dataset.optionName;
          if (!optionName) return;

          const currentGroup = currentGroups.find(group => group.dataset.optionName === optionName);
          if (!currentGroup) return;

          this.syncOptionGroup(currentGroup, incomingGroup);
        });
      }

      syncOptionGroup(currentGroup, incomingGroup) {
        this.syncOptionHeader(currentGroup, incomingGroup);

        if (incomingGroup.tagName === 'FIELDSET') {
          this.syncRadioOptionGroup(currentGroup, incomingGroup);
        } else {
          this.syncDropdownOptionGroup(currentGroup, incomingGroup);
        }
      }

      syncOptionHeader(currentGroup, incomingGroup) {
        const currentSelectedValue = currentGroup.querySelector('[data-selected-value]');
        const incomingSelectedValue = incomingGroup.querySelector('[data-selected-value]');

        if (currentSelectedValue && incomingSelectedValue) {
          if (incomingSelectedValue.childElementCount > 0) {
            currentSelectedValue.innerHTML = incomingSelectedValue.innerHTML;
          } else {
            currentSelectedValue.textContent = incomingSelectedValue.textContent;
          }

          if (incomingSelectedValue.hasAttribute('data-selected-value')) {
            currentSelectedValue.setAttribute('data-selected-value', incomingSelectedValue.getAttribute('data-selected-value'));
          } else {
            currentSelectedValue.removeAttribute('data-selected-value');
          }
        }

        const currentDetail = currentGroup.querySelector('[data-option-detail]');
        const incomingDetail = incomingGroup.querySelector('[data-option-detail]');

        if (currentDetail && incomingDetail) {
          currentDetail.innerHTML = incomingDetail.innerHTML;
        }
      }

      syncRadioOptionGroup(currentGroup, incomingGroup) {
        const currentInputs = Array.from(currentGroup.querySelectorAll('input[type="radio"]'));
        const incomingInputs = Array.from(incomingGroup.querySelectorAll('input[type="radio"]'));

        incomingInputs.forEach(incomingInput => {
          const value = incomingInput.value;
          const currentInput = currentInputs.find(input => input.value === value);

          if (currentInput) {
            currentInput.checked = incomingInput.checked;
            currentInput.disabled = incomingInput.disabled;

            const currentLabel = currentGroup.querySelector(`label[for="${currentInput.id}"]`);
            const incomingLabel = incomingGroup.querySelector(`label[for="${incomingInput.id}"]`);

            if (currentLabel && incomingLabel) {
              Array.from(incomingLabel.attributes).forEach(attr => {
                if (attr.name.startsWith('data-')) {
                  currentLabel.setAttribute(attr.name, attr.value);
                }
              });

              if (incomingLabel.classList.contains('disabled')) {
                currentLabel.classList.add('disabled');
              } else {
                currentLabel.classList.remove('disabled');
              }
            }
          }
        });
      }

      syncDropdownOptionGroup(currentGroup, incomingGroup) {
        const currentSelect = currentGroup.querySelector('select');
        const incomingSelect = incomingGroup.querySelector('select');

        if (!currentSelect || !incomingSelect) return;

        const currentOptions = Array.from(currentSelect.options);
        const incomingOptions = Array.from(incomingSelect.options);

        incomingOptions.forEach((incomingOption, index) => {
          const currentOption = currentOptions[index];
          if (currentOption) {
            currentOption.selected = incomingOption.selected;
            currentOption.disabled = incomingOption.disabled;

            Array.from(incomingOption.attributes).forEach(attr => {
              if (attr.name.startsWith('data-')) {
                currentOption.setAttribute(attr.name, attr.value);
              }
            });
          }
        });

        currentSelect.value = incomingSelect.value;
      }

      handleUpdateProductInfo(productUrl) {
        return html => {
          const variant = this.getSelectedVariant(html);
          console.log('[Variant Change] Selected variant:', variant);

          const mappedEntries = this.getVariantImageEntries(variant);
          console.log('[Variant Change] Mapped entries:', mappedEntries);

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price);
          this.updateVariantDetails(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // Update price and other info BEFORE any gallery logic
          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                destination.classList.toggle("hidden", inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1);
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");
          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });

          // Priority 1: If we have a variant image map mapping, use it and skip gallery fetch
          if (mappedEntries) {
            const mapKey = this.buildVariantImageMapKey(variant);
            console.log('[Variant Image Map] 🎯 Using mapped images for variant:', {
              key: mapKey,
              imageCount: mappedEntries.length,
              images: mappedEntries.map(e => e.view)
            });
            this.renderVariantImageSlides(mappedEntries, variant);
            return; // Exit early to prevent server fetch
          }

          // Priority 2: No mapping - fetch full gallery from server
          console.log('[Variant Image Map] ⬇️ No mapping found, fetching gallery from server');
          if (variant && variant.id) {
            const destinationGallery = this.productMediaGallery;

            // Add loading state for CSS opacity transition
            destinationGallery?.classList.add('is-loading');

            fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`)
              .then(r => r.text())
              .then(txt => {
                const doc = new DOMParser().parseFromString(txt, 'text/html');
                const sourceGallery = doc.querySelector(`#MediaGallery-${this.sectionId}`);
                if (sourceGallery && destinationGallery) {
                  const postProcess = [
                    ...this.postProcessHtmlCallbacks,
                    (newNode) => {
                      this.productMediaGallery = newNode;
                      this.refreshGalleryLoader();

                      // Reset cached states since the gallery is replaced
                      this.originalSliderHTML = null;
                      this.originalGridHTML = null;
                      this.originalThumbsHTML = null;

                      newNode.classList.add('is-loading');

                      const thumbsEl = newNode.querySelector('swiper-product-thumbs');
                      if (thumbsEl && typeof thumbsEl.init === 'function') thumbsEl.init();
                      const galleryEl = newNode.querySelector('swiper-product-gallery');
                      if (galleryEl && typeof galleryEl.init === 'function') galleryEl.init();

                      if (galleryEl?.instance) {
                        try {
                          galleryEl.instance.slideTo(0, 0);
                        } catch (e) {
                          console.warn('Swiper slideTo failed', e);
                        }
                      }

                      this.cacheOriginalGalleryState();
                      this.prepareGalleryImages(newNode);

                      const finalize = () => {
                        this.hideVariantImageLoadingSpinner();
                        this.hasInitializedVariantGallery = true;
                      };

                      this.waitForPrimaryImageLoad(newNode, finalize);
                      setTimeout(finalize, 2000);
                    }
                  ];

                  HTMLUpdateUtility.viewTransition(
                    destinationGallery,
                    sourceGallery,
                    this.preProcessHtmlCallbacks,
                    postProcess
                  );
                } else {
                  destinationGallery?.classList.remove('is-loading');
                  this.hideVariantImageLoadingSpinner();
                }
              })
              .catch((err) => {
                console.error('[Variant Image Map] Gallery fetch failed:', err);
                destinationGallery?.classList.remove('is-loading');
                this.hideVariantImageLoadingSpinner();
                // Fallback to legacy hide/show method
                if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
                  this.updateVariantMedia(html);
                }
              });
          } else if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
            // Fallback to legacy hide/show if no variant ID
            this.updateVariantMedia(html);
          } else {
            // No mapping, no variant, no legacy mode - just update media if possible
            this.updateMedia(variant?.featured_media?.id);
          }
        };
      }

      updateVariantMedia() {
        // Skip if variant image map is active - it handles gallery rendering
        if (this.productMediaGallery?.dataset?.variantImageMapActive === 'true') {
          return;
        }

        const select_variant_options_handle = this.querySelector(
          'product-variant-selects [data-selected-variant-options-handle]'
        )?.innerHTML.trim();

        if (!select_variant_options_handle) {
          const productAllVariants = this.productVariantSelects.querySelector('[data-all-variants]').innerHTML.trim();

          // Make sure to attach the event listener to the correct element
          this.productVariantSelects.addEventListener('click', (event) => {
            const closestVariantOptionInput = event.target.closest('.product-option__input');

            // Ensure that you are finding the correct input
            if (closestVariantOptionInput) {
              const closestVariantOptionInputValue = closestVariantOptionInput.value;

              // Parse productAllVariants and check for the selected variant
              const availableVariant = JSON.parse(productAllVariants).find(variant => {
                return variant.options.some(option => option === closestVariantOptionInputValue);
              });
            } else {
              // console.log('No variant option input found');
            }
          });
        }

        this.productMediaGallery.querySelectorAll('[data-media-group]').forEach(element => element.classList.add('hidden'));

        this.productMediaGallery.querySelectorAll(`[data-media-group="${select_variant_options_handle}"]`).forEach(element => element.classList.remove('hidden'));

        this.productMediaGallery.querySelectorAll('swiper-product-gallery').forEach(swiperSlide => {
          if (swiperSlide.instance) {
            swiperSlide.instance.update(); // Update the Swiper instance
          } else {
            swiperSlide.init(); // If the instance is not available, reinitialize
          }
        });
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ""
          }`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              productVariantDetail.innerHTML = productVariantDetailsData?.[variantId]?.replaceAll('\n', '<br />') || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount");
        if (!discountBadge) return;


        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // Disabled auto-scroll on variant change - keeps user's scroll position
        // if (
        //   this.querySelector(".main-product__media")?.hasAttribute(
        //     "data-desktop-grid"
        //   ) && window.innerWidth >= 750
        // ) {
        //   this.scrollToImage(variantFeaturedMediaId);
        // }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "nearest",
              inline: "nearest",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      // ==========================================
      // VARIANT IMAGE MAP METHODS
      // ==========================================

      parseVariantImageMap() {
        const script = this.querySelector('script[data-variant-image-map]');
        if (!script) {
          console.log('[Variant Image Map] No metafield found for this product');
          return null;
        }

        try {
          const parsed = JSON.parse(script.textContent);
          if (!parsed) {
            console.warn('[Variant Image Map] Metafield script is empty');
            return null;
          }

          // Handle both direct objects and metafield wrapper objects
          const data =
            typeof parsed === 'object' && parsed !== null && parsed.value && typeof parsed.value === 'object'
              ? parsed.value
              : parsed;

          if (!data || typeof data !== 'object') {
            console.warn('[Variant Image Map] Invalid data structure:', data);
            return null;
          }

          // Create case-insensitive lookups AND normalized key map
          this.variantImageMapShapeLookup = new Map();
          this.variantImageMapMetalLookup = new Map();
          this.variantImageMapNormalized = new Map();

          Object.keys(data).forEach(originalKey => {
            const parts = originalKey.split('|');
            if (parts.length < 2) {
              return;
            }

            const shape = parts[0]?.trim();
            const metal = parts[1]?.trim();

            if (shape && metal) {
              // Store lowercase -> original case mapping for lookups
              this.variantImageMapShapeLookup.set(shape.toLowerCase(), shape);
              this.variantImageMapMetalLookup.set(metal.toLowerCase(), metal);

              // Store normalized key (lowercase) -> original key
              const normalizedKey = `${shape.toLowerCase()}|${metal.toLowerCase()}`;
              this.variantImageMapNormalized.set(normalizedKey, originalKey);
            }
          });
          console.log('[Variant Image Map] ✅ Loaded successfully!', {
            totalKeys: Object.keys(data).length,
            sampleKeys: Object.keys(data).slice(0, 3)
          });
          return data;
        } catch (error) {
          console.error('[Variant Image Map] Failed to parse metafield JSON:', error);
          return null;
        }
      }

      buildVariantImageMapKey(variant) {
        if (!variant) {
          console.log('[Variant Image Map Key] No variant provided');
          return null;
        }

        if (!this.variantImageMap) {
          console.log('[Variant Image Map Key] No variant image map found');
          return null;
        }

        // Collect ALL variant option values (case-insensitive)
        const optionValues = [];

        // Method 1: Try option1, option2, option3 properties
        ['option1', 'option2', 'option3'].forEach(key => {
          if (variant[key]) {
            optionValues.push(typeof variant[key] === 'string' ? variant[key].trim() : variant[key]);
          }
        });

        // Method 2: Try options array (fallback)
        if (Array.isArray(variant.options) && variant.options.length) {
          variant.options.forEach(value => {
            if (value) {
              optionValues.push(typeof value === 'string' ? value.trim() : value);
            }
          });
        }

        // Deduplicate and normalize
        const uniqueValues = [...new Set(optionValues)]
          .map(value => (value && value.toString ? value.toString().trim() : ''))
          .filter(Boolean);

        console.log('[Variant Image Map Key] Option values:', uniqueValues);

        if (!uniqueValues.length) {
          console.log('[Variant Image Map Key] No option values found');
          return null;
        }

        // Match shape and metal using CASE-INSENSITIVE lookups
        let shapeValue = null;
        let metalValue = null;

        uniqueValues.forEach(value => {
          const lower = value.toLowerCase();

          // Find shape (if not already found)
          if (!shapeValue && this.variantImageMapShapeLookup?.has(lower)) {
            shapeValue = this.variantImageMapShapeLookup.get(lower);
            console.log('[Variant Image Map Key] Found shape:', shapeValue);
          }

          // Find metal (if not already found)
          if (!metalValue && this.variantImageMapMetalLookup?.has(lower)) {
            metalValue = this.variantImageMapMetalLookup.get(lower);
            console.log('[Variant Image Map Key] Found metal:', metalValue);
          }
        });

        // If we found both shape and metal, build the key
        if (shapeValue && metalValue) {
          const normalizedKey = `${shapeValue.toLowerCase()}|${metalValue.toLowerCase()}`;
          const originalKey = this.variantImageMapNormalized.get(normalizedKey);

          console.log('[Variant Image Map Key] Normalized key:', normalizedKey);
          console.log('[Variant Image Map Key] Original key:', originalKey);
          console.log('[Variant Image Map Key] Key exists in map:', !!this.variantImageMap[originalKey]);

          if (originalKey && this.variantImageMap[originalKey]) {
            return originalKey;
          }
        } else {
          console.log('[Variant Image Map Key] Missing shape or metal:', { shapeValue, metalValue });
        }

        return null;
      }

      getVariantImageEntries(variant) {
        if (!variant || !this.variantImageMap) return null;
        const key = this.buildVariantImageMapKey(variant);
        if (!key) return null;
        const entries = this.variantImageMap[key];
        return Array.isArray(entries) && entries.length ? entries : null;
      }

      applyVariantImageMap(variant) {
        const entries = this.getVariantImageEntries(variant);
        if (entries) {
          const mapKey = this.buildVariantImageMapKey(variant);
          console.log('[Variant Image Map] 🚀 Initial load - applying mapped images:', {
            key: mapKey,
            imageCount: entries.length,
            images: entries.map(e => e.view)
          });
          this.renderVariantImageSlides(entries, variant, {
            suppressSpinner: !this.hasInitializedVariantGallery
          });
          return true;
        }
        this.restoreOriginalGallery();
        return false;
      }

      cacheOriginalGalleryState() {
        // Cache both slider AND grid separately (both exist in DOM)
        if (this.originalSliderHTML !== null || this.originalGridHTML !== null) {
          return;
        }

        if (!this.productMediaGallery) {
          return;
        }

        // Detect gallery elements - BOTH can exist (CSS shows/hides)
        const galleryComponent = this.productMediaGallery.querySelector('swiper-product-gallery');
        const thumbsComponent = this.productMediaGallery.querySelector('swiper-product-thumbs');
        const gridGallery = this.productMediaGallery.querySelector('.main-product__media--grid');

        // Cache Swiper slider gallery (mobile view)
        if (galleryComponent) {
          const wrapper = galleryComponent.querySelector('.swiper-wrapper');
          if (wrapper) {
            this.originalSliderHTML = wrapper.innerHTML;
          }
        }

        // Cache Grid gallery (desktop view)
        if (gridGallery) {
          this.originalGridHTML = gridGallery.innerHTML;
        }

        // Cache thumbnails (if using slider)
        if (thumbsComponent) {
          const wrapper = thumbsComponent.querySelector('.swiper-wrapper');
          if (wrapper) {
            this.originalThumbsHTML = wrapper.innerHTML;
          }
        }
      }

      refreshGalleryLoader() {
        if (!this.productMediaGallery) {
          this.galleryLoader = null;
          return null;
        }
        this.galleryLoader = this.productMediaGallery.querySelector('[data-media-loader]');
        return this.galleryLoader;
      }

      ensureGalleryLoader() {
        if (!this.productMediaGallery) return null;
        if (!this.galleryLoader || !this.productMediaGallery.contains(this.galleryLoader)) {
          return this.refreshGalleryLoader();
        }
        return this.galleryLoader;
      }

      lockGalleryHeight(fallbackHeight = null) {
        // Simplified - just return the current height for reference, don't set min-height
        if (!this.productMediaGallery) return null;
        return Math.round(this.productMediaGallery.getBoundingClientRect().height);
      }

      unlockGalleryHeight() {
        // No-op now since we're not locking heights
      }

      getPrimaryMediaImage(container) {
        if (!container) return null;
        return (
          container.querySelector('.swiper-slide[data-variant-image-order="0"] img') ||
          container.querySelector('.swiper-slide[data-selected] img') ||
          container.querySelector('.main-product__media--grid-item:not(.hidden) img') ||
          container.querySelector('.main-product__media--grid img') ||
          container.querySelector('img')
        );
      }

      waitForPrimaryImageLoad(container, callback, timeout = 5000) {
        const targetImage = this.getPrimaryMediaImage(container);
        if (!targetImage) {
          callback?.();
          return;
        }

        if (targetImage.complete && targetImage.naturalWidth > 0) {
          callback?.();
          return;
        }

        let timeoutId;
        const finalize = () => {
          if (timeoutId) clearTimeout(timeoutId);
          targetImage.removeEventListener('load', finalize);
          targetImage.removeEventListener('error', finalize);
          callback?.();
        };

        targetImage.addEventListener('load', finalize, { once: true });
        targetImage.addEventListener('error', finalize, { once: true });
        timeoutId = setTimeout(finalize, timeout);
      }

      prepareGalleryImages(container) {
        if (!container) return;
        const images = Array.from(container.querySelectorAll('img'));
        if (!images.length) return;

        const targets = images.filter(img => img.closest('.main-product__media') && img.closest('.media'));
        if (!targets.length) return;

        targets.forEach(img => {
          img.setAttribute('data-variant-media', 'true');
        });

        this.animateGalleryImages(targets);
      }

      animateGalleryImages(targets) {
        if (!Array.isArray(targets) || !targets.length) return;

        targets.forEach(img => {
          img.classList.remove('is-loaded');

          const markLoaded = () => {
            requestAnimationFrame(() => img.classList.add('is-loaded'));
          };

          if (img.complete && img.naturalWidth > 0) {
            markLoaded();
          } else {
            const finalize = () => {
              markLoaded();
            };
            img.addEventListener('load', finalize, { once: true });
            img.addEventListener('error', finalize, { once: true });
          }
        });
      }

      showVariantImageLoadingSpinner(fallbackHeight = null) {
        if (!this.productMediaGallery) return null;
        const appliedHeight = this.lockGalleryHeight(fallbackHeight);
        const loader = this.ensureGalleryLoader();
        if (loader) {
          this.productMediaGallery.classList.add('is-loading');
          loader.removeAttribute('hidden');
          requestAnimationFrame(() => {
            loader.classList.add('active');
          });
        }
        return appliedHeight;
      }

      hideVariantImageLoadingSpinner() {
        if (!this.productMediaGallery) return;
        const loader = this.ensureGalleryLoader();
        if (loader) {
          loader.classList.remove('active');
          setTimeout(() => {
            if (!loader.classList.contains('active')) {
              loader.setAttribute('hidden', '');
            }
          }, 200);
        }
        // Remove loading state after short delay for CSS transition
        setTimeout(() => {
          this.productMediaGallery?.classList.remove('is-loading');
        }, 150);
      }

      renderVariantImageSlides(entries, variant, options = {}) {
        console.log('[renderVariantImageSlides] START - entries:', entries?.length, 'variant:', variant, 'options:', options);

        if (!this.productMediaGallery || !Array.isArray(entries) || !entries.length) {
          console.log('[renderVariantImageSlides] ABORT - Missing gallery or entries');
          return;
        }

        // Only add loading state for variant changes, not initial page load
        const shouldShowLoadingState = !options.suppressSpinner && this.hasInitializedVariantGallery;
        if (shouldShowLoadingState) {
          this.productMediaGallery.classList.add('is-loading');
        }

        // Detect layout type
        const galleryComponent = this.productMediaGallery.querySelector('swiper-product-gallery');
        const thumbsComponent = this.productMediaGallery.querySelector('swiper-product-thumbs');
        const gridGallery = this.productMediaGallery.querySelector('.main-product__media--grid');
        const key = this.buildVariantImageMapKey(variant);

        console.log('[renderVariantImageSlides] Gallery components:', {
          hasGalleryComponent: !!galleryComponent,
          hasThumbsComponent: !!thumbsComponent,
          hasGridGallery: !!gridGallery,
          key
        });

        const buildSlideMarkup = (entry, index, isThumb = false, forGrid = false) => {
          const viewLabel = entry?.view || `Image ${index + 1}`;
          const variantTitle = variant?.title || '';
          const altText = variantTitle ? `${variantTitle} – ${viewLabel}` : viewLabel;
          const isFirst = index === 0;
          const mediaId = `variant-map-${variant?.id || 'unknown'}-${index}`;

          const loading = isThumb ? 'lazy' : isFirst ? 'eager' : 'lazy';
          const fetchPriority = !isThumb && !forGrid && isFirst ? ' fetchpriority="high"' : '';
          let selectedAttr = '';
          if (isFirst && !forGrid) {
            selectedAttr = ' data-selected';
          }

          const widths = isThumb ? [80, 120, 160, 240] : [360, 480, 600, 720, 840, 960, 1280, 1440];
          const srcset = widths.map(w => `${entry.src}?width=${w} ${w}w`).join(', ');
          const sizes = isThumb ? '96px' : '(max-width: 360px) 360px,(max-width: 480px) 480px,(max-width: 600px) 600px,(max-width: 720px) 720px,(max-width: 840px) 840px,(max-width: 960px) 960px,(max-width: 1280px) 1280px,(max-width: 1440px) 1440px, 100vw';
          const baseSrcWidth = isThumb ? 160 : 128;
          const widthAttr = isThumb ? ' width="160" height="160"' : ' width="4000" height="4000"';
          const gridStyle = forGrid ? ' style="width: 100% !important; height: auto !important; display: block !important;"' : '';
          const variantViewAttr = viewLabel ? ` data-variant-view="${viewLabel.toLowerCase()}"` : '';

          const zoomOverlay = `
            <div class="media__overlay">
              <div class="media__type--badge main-product__media--feat color-scheme-7 gradient" title="Image"></div>
              <div class="product__gallery-opener main-product__media--feat no-js-hidden color-scheme-7 gradient" data-modal-opener-id="image-${mediaId}">
                <a href="${entry.src}?width=4000" class="product__gallery-toggle motion-reduce" type="button" aria-haspopup="dialog" data-media-id="${mediaId}" data-pswp-image="" data-pswp-src="${entry.src}?width=4000" data-pswp-width="4000" data-pswp-height="4000" target="_blank">
                  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 10L4 4M4 10V4H10M14 14L20 20M20 14V20H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="square"></path>
                  </svg>
                  <span class="visually-hidden">Open media ${index} in modal</span>
                </a>
              </div>
            </div>`;

          const thumbOverlay = `
            <div class="media__overlay">
              <div class="media__type--badge main-product__media--feat" title="Image"></div>
            </div>`;

          const imageTag = `<img data-variant-media="true" src="${entry.src}?width=${baseSrcWidth}" alt="${altText}"${widthAttr} loading="${loading}" srcset="${srcset}" sizes="${sizes}"${fetchPriority}${variantViewAttr}${gridStyle}>`;

          if (forGrid) {
            return `<div class="main-product__media--grid-item">
              <div class="swiper-slide" data-media-id="${mediaId}" data-media-type="image" data-media-group="" data-index="${index}">
                <div class="media">
                  ${zoomOverlay}
                  ${imageTag}
                </div>
              </div>
            </div>`;
          }

          if (isThumb) {
            return `<div class="swiper-slide" data-media-id="${mediaId}" data-media-type="image" data-index="${index}"${selectedAttr}>
              <div class="media media--thumb">
                ${thumbOverlay}
                ${imageTag}
              </div>
            </div>`;
          }

          return `<div class="swiper-slide" data-variant-image-order="${index}" data-media-id="${mediaId}" data-media-type="image"${selectedAttr}>
            <div class="media">
              ${zoomOverlay}
              ${imageTag}
            </div>
          </div>`;
        };

        // Update slider, grid, and thumbnail galleries when present

        // Handle Swiper Slider Gallery (mobile view)
        if (galleryComponent) {
          console.log('[Mobile Swiper] Updating gallery component');
          const wrapper = galleryComponent.querySelector('.swiper-wrapper');

          let instance = galleryComponent.instance;
          console.log('[Mobile Swiper] Instance exists:', !!instance);

          if (!instance && wrapper) {
            // No instance exists - just update innerHTML and try to initialize
            console.log('[Mobile Swiper] No instance - updating innerHTML and initializing');
            wrapper.innerHTML = entries.map((entry, index) => buildSlideMarkup(entry, index, false, false)).join('');
            galleryComponent.swiperInitialized = false;
            if (typeof galleryComponent.init === 'function') {
              galleryComponent.init();
              console.log('[Mobile Swiper] Initialized');
            }
          } else if (instance && wrapper) {
            // Instance exists - use Swiper's update method
            console.log('[Mobile Swiper] Instance exists - updating slides');

            // Remove all slides first
            if (typeof instance.removeAllSlides === 'function') {
              instance.removeAllSlides();
            } else {
              // Fallback - update wrapper innerHTML
              wrapper.innerHTML = entries.map((entry, index) => buildSlideMarkup(entry, index, false, false)).join('');
            }

            // Add new slides
            if (typeof instance.removeAllSlides === 'function' && typeof instance.appendSlide === 'function') {
              const slideMarkups = entries.map((entry, index) => buildSlideMarkup(entry, index, false, false));
              slideMarkups.forEach(markup => instance.appendSlide(markup));
            }

            // Update swiper
            if (typeof instance.update === 'function') {
              instance.update();
            }

            // Go to first slide
            if (typeof instance.slideTo === 'function') {
              instance.slideTo(0, 0);
            }

            console.log('[Mobile Swiper] Update complete');
          } else {
            console.warn('[Mobile Swiper] No wrapper found');
          }
        } else {
          console.warn('[Mobile Swiper] No galleryComponent found');
        }

        // Handle Grid Gallery (desktop view)
        if (gridGallery) {
          const newHTML = entries.map((entry, index) => buildSlideMarkup(entry, index, false, true)).join('');
          gridGallery.innerHTML = newHTML;
        }

        if (thumbsComponent) {
          const thumbsWrapper = thumbsComponent.querySelector('.swiper-wrapper');
          let thumbsInstance = thumbsComponent.instance;

          if (!thumbsInstance && thumbsWrapper) {
            // No instance - update innerHTML and initialize
            thumbsWrapper.innerHTML = entries.map((entry, index) => buildSlideMarkup(entry, index, true, false)).join('');
            thumbsComponent.swiperInitialized = false;
            if (typeof thumbsComponent.init === 'function') {
              thumbsComponent.init();
            }
          } else if (thumbsInstance && thumbsWrapper) {
            // Instance exists - use Swiper's update method
            if (typeof thumbsInstance.removeAllSlides === 'function') {
              thumbsInstance.removeAllSlides();
            } else {
              thumbsWrapper.innerHTML = entries.map((entry, index) => buildSlideMarkup(entry, index, true, false)).join('');
            }

            if (typeof thumbsInstance.removeAllSlides === 'function' && typeof thumbsInstance.appendSlide === 'function') {
              const slideMarkups = entries.map((entry, index) => buildSlideMarkup(entry, index, true, false));
              slideMarkups.forEach(markup => thumbsInstance.appendSlide(markup));
            }

            if (typeof thumbsInstance.update === 'function') {
              thumbsInstance.update();
            }

            if (typeof thumbsInstance.slideTo === 'function') {
              thumbsInstance.slideTo(0, 0);
            }
          }

          // Update gallery thumbs connection
          if (galleryComponent?.instance?.thumbs && typeof galleryComponent.instance.thumbs.update === 'function') {
            galleryComponent.instance.thumbs.update();
          }
        }

        this.prepareGalleryImages(this.productMediaGallery);

        this.productMediaGallery.dataset.variantImageMapActive = 'true';
        this.currentVariantImageKey = key;

        console.log('[renderVariantImageSlides] COMPLETE - Set variantImageMapActive = true, key:', key);

        // Wait for first image to load, then remove loading state
        let finalized = false;
        const cleanup = () => {
          if (finalized) return;
          finalized = true;
          this.hideVariantImageLoadingSpinner();
          this.hasInitializedVariantGallery = true;
          console.log('[renderVariantImageSlides] Cleanup complete');
        };

        this.waitForPrimaryImageLoad(this.productMediaGallery, cleanup);
        setTimeout(cleanup, 2000);
      }

      restoreOriginalGallery() {
        if (!this.productMediaGallery) {
          return;
        }

        const galleryComponent = this.productMediaGallery.querySelector('swiper-product-gallery');
        const thumbsComponent = this.productMediaGallery.querySelector('swiper-product-thumbs');
        const gridGallery = this.productMediaGallery.querySelector('.main-product__media--grid');

        if (!this.originalSliderHTML && !this.originalGridHTML) {
          return;
        }

        // Restore Swiper Slider Gallery (mobile view)
        if (this.originalSliderHTML !== null && galleryComponent) {
          const wrapper = galleryComponent.querySelector('.swiper-wrapper');
          if (wrapper) {
            wrapper.innerHTML = this.originalSliderHTML;

            const instance = galleryComponent.instance;
            if (instance) {
              if (typeof instance.updateSlides === 'function') instance.updateSlides();
              if (typeof instance.update === 'function') instance.update();
              if (typeof instance.slideTo === 'function') instance.slideTo(0, 0);
            }
          }
        }

        // Restore Grid Gallery (desktop view)
        if (this.originalGridHTML !== null && gridGallery) {
          gridGallery.innerHTML = this.originalGridHTML;
        }

        // Restore thumbnails
        if (this.originalThumbsHTML !== null && thumbsComponent) {
          const thumbsWrapper = thumbsComponent.querySelector('.swiper-wrapper');
          if (thumbsWrapper) {
            thumbsWrapper.innerHTML = this.originalThumbsHTML;

            const thumbsInstance = thumbsComponent.instance;
            if (thumbsInstance) {
              if (typeof thumbsInstance.updateSlides === 'function') thumbsInstance.updateSlides();
              if (typeof thumbsInstance.update === 'function') thumbsInstance.update();
              if (typeof thumbsInstance.slideTo === 'function') thumbsInstance.slideTo(0, 0);
            }

            if (galleryComponent?.instance?.thumbs && typeof galleryComponent.instance.thumbs.update === 'function') {
              galleryComponent.instance.thumbs.update();
            }
          }
        }

        delete this.productMediaGallery.dataset.variantImageMapActive;
        this.currentVariantImageKey = null;
        this.hasInitializedVariantGallery = true;
      }
    }
  );
}

// Enhanced ProductForm class with conditional ring size validation
if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        
        // Only validate ring size for product forms, not cart drawer operations
        const isProductForm = this.closest('product-info') !== null;
        const requiresRingSize = isProductForm && this.productRequiresRingSize();
        
        // Validate custom fields only if this is a product form submission
        if (requiresRingSize && !this.validateCustomFields()) {
          return; // Validation failed, don't proceed
        }

        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();
        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        
        // Only ensure custom fields for product forms
        if (isProductForm) {
          this.ensureCustomFieldsInFormData(formData);
        }
        
        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(
                ".sold-out-message"
              );
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Success - clear saved custom field data
            if (isProductForm) {
              this.clearCustomFieldData();
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error)
              this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add(
              "hidden"
            );
          });
      }

      productRequiresRingSize() {
        // Check if this product needs a ring size (you can customize this logic)
        // For example, check if the ring size field exists on the page
        return document.getElementById('RingSize') !== null;
      }

      validateCustomFields() {
        // Validate ring size
        const ringSizeSelect = this.form.querySelector('#RingSize');
        if (ringSizeSelect && !ringSizeSelect.value) {
          this.showRingSizeError();
          return false;
        }
        
        this.clearRingSizeError();
        return true;
      }

      ensureCustomFieldsInFormData(formData) {
        // Ensure ring size is included
        const ringSizeSelect = this.form.querySelector('#RingSize');
        if (ringSizeSelect && ringSizeSelect.value) {
          // Remove any existing ring size entries to avoid duplicates
          formData.delete('properties[Ring Size]');
          formData.append('properties[Ring Size]', ringSizeSelect.value);
        }

        // Ensure custom engraving is included
        const customEngraving = this.form.querySelector('#CustomEngraving');
        if (customEngraving && customEngraving.value) {
          // The field should already have the correct name, but ensure it
          customEngraving.name = 'properties[Custom Engraving]';
        }

        // Ensure all Infinite Options fields are properly included
        // This handles Size and any other custom fields from Infinite Options app
        const infiniteOptionsContainer = document.getElementById('infiniteoptions-container');
        if (infiniteOptionsContainer) {
          // Find all input fields within the Infinite Options container
          const infiniteOptionsFields = infiniteOptionsContainer.querySelectorAll('input[name^="properties"], select[name^="properties"], textarea[name^="properties"]');
          
          infiniteOptionsFields.forEach(field => {
            // Handle different input types
            let shouldInclude = false;
            let fieldValue = '';
            
            if (field.type === 'checkbox' || field.type === 'radio') {
              // Only include if checked
              if (field.checked) {
                shouldInclude = true;
                fieldValue = field.value;
              }
            } else {
              // For text, select, textarea - include if has value
              if (field.value) {
                shouldInclude = true;
                fieldValue = field.value;
              }
            }
            
            if (shouldInclude && field.name) {
              // Extract the property name from the field name (e.g., properties[Size])
              const propertyMatch = field.name.match(/properties\[(.*?)\]/);
              if (propertyMatch && propertyMatch[1]) {
                const propertyName = propertyMatch[1];
                // Remove any existing entries to avoid duplicates
                formData.delete(field.name);
                // Add the current value
                formData.append(field.name, fieldValue);
                console.log(`Infinite Options field ensured: ${propertyName} = ${fieldValue}`);
              }
            }
          });
        }
      }

      showRingSizeError() {
        const ringSizeError = document.getElementById('ring-size-error');
        const ringSizeSelect = document.getElementById('RingSize');
        
        if (ringSizeError) {
          ringSizeError.style.display = 'block';
        }
        
        if (ringSizeSelect) {
          ringSizeSelect.style.borderColor = '#ff3860';
          ringSizeSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      clearRingSizeError() {
        const ringSizeError = document.getElementById('ring-size-error');
        const ringSizeSelect = document.getElementById('RingSize');
        
        if (ringSizeError) {
          ringSizeError.style.display = 'none';
        }
        
        if (ringSizeSelect) {
          ringSizeSelect.style.borderColor = '#333';
        }
      }

      clearCustomFieldData() {
        sessionStorage.removeItem('ringSize');
        sessionStorage.removeItem('engravingText');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = this.hasAttribute(
            "data-has-selling-plan"
          )
            ? window.variantStrings.addSubscriptionToCart
            : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

// Enhanced form submission handler with context awareness
(function() {
  'use strict';
  
  // Function to check if the request is from a product page
  function isProductPageRequest(source) {
    // Check various indicators that this is a product page request
    if (source instanceof FormData) {
      // Check if the form has ring size field
      const formElement = document.querySelector('form[action*="/cart/add"]:not(.cart-drawer__form)');
      if (formElement && formElement.querySelector('#RingSize')) {
        return true;
      }
    }
    
    // Check if we're in a product-info context
    return document.querySelector('product-info') !== null && 
           document.querySelector('#RingSize') !== null;
  }
  
  // Function to ensure ring size is always included in form data (only for product pages)
  function ensureRingSizeInFormData(formData, isProductPage) {
    if (!isProductPage) return false;
    
    const ringSizeSelect = document.getElementById('RingSize');
    
    if (ringSizeSelect && ringSizeSelect.value) {
      // Remove any existing ring size entries to avoid duplicates
      formData.delete('properties[Ring Size]');
      // Add the current ring size value
      formData.append('properties[Ring Size]', ringSizeSelect.value);
      console.log('Ring size added to form data:', ringSizeSelect.value);
      return true;
    }
    return false;
  }
  
  // Function to ensure all Infinite Options fields are included
  function ensureInfiniteOptionsInFormData(formData, isProductPage) {
    if (!isProductPage) return;
    
    const infiniteOptionsContainer = document.getElementById('infiniteoptions-container');
    if (!infiniteOptionsContainer) return;
    
    // Find all input fields within the Infinite Options container
    const fields = infiniteOptionsContainer.querySelectorAll('input[name^="properties"], select[name^="properties"], textarea[name^="properties"]');
    
    fields.forEach(field => {
      // Handle different input types
      let shouldInclude = false;
      let fieldValue = '';
      
      if (field.type === 'checkbox' || field.type === 'radio') {
        // Only include if checked
        if (field.checked) {
          shouldInclude = true;
          fieldValue = field.value;
        }
      } else {
        // For text, select, textarea - include if has value
        if (field.value) {
          shouldInclude = true;
          fieldValue = field.value;
        }
      }
      
      if (shouldInclude && field.name) {
        // Extract the property name from the field name
        const propertyMatch = field.name.match(/properties\[(.*?)\]/);
        if (propertyMatch && propertyMatch[1]) {
          const propertyName = propertyMatch[1];
          // Remove any existing entries to avoid duplicates
          formData.delete(field.name);
          // Add the current value
          formData.append(field.name, fieldValue);
          console.log(`Infinite Options field ensured in fetch: ${propertyName} = ${fieldValue}`);
        }
      }
    });
  }
  
  // Intercept fetch requests with context awareness
  const originalFetch = window.fetch;
  window.fetch = function(url, options) {
    // Check if this is a cart add request
    if (url && (url.includes('/cart/add') || url.includes('cart/add.js'))) {
      
      // Determine if this is from a product page
      const isFromProductPage = isProductPageRequest(options?.body);
      
      // Only validate ring size for product page requests
      if (isFromProductPage) {
        console.log('Intercepting product page cart/add request');
        
        // Handle FormData
        if (options && options.body instanceof FormData) {
          const ringSizeSelect = document.getElementById('RingSize');
          
          // Only validate if ring size field exists and is empty
          if (ringSizeSelect && !ringSizeSelect.value) {
            // Instead of blocking, we'll let the ProductForm handle validation
            console.log('Ring size validation will be handled by ProductForm');
          } else {
            ensureRingSizeInFormData(options.body, true);
          }
          
          // Always ensure Infinite Options fields are included
          ensureInfiniteOptionsInFormData(options.body, true);
        }
        
        // Handle JSON requests
        if (options && options.body && typeof options.body === 'string') {
          try {
            const data = JSON.parse(options.body);
            
            // Ensure properties object exists
            if (!data.properties) {
              data.properties = {};
            }
            
            // Add ring size if present
            const ringSizeSelect = document.getElementById('RingSize');
            if (ringSizeSelect && ringSizeSelect.value) {
              data.properties['Ring Size'] = ringSizeSelect.value;
              console.log('Ring size added to JSON request:', ringSizeSelect.value);
            }
            
            // Add all Infinite Options fields
            const infiniteOptionsContainer = document.getElementById('infiniteoptions-container');
            if (infiniteOptionsContainer) {
              const fields = infiniteOptionsContainer.querySelectorAll('input[name^="properties"], select[name^="properties"], textarea[name^="properties"]');
              
              fields.forEach(field => {
                // Handle different input types
                let shouldInclude = false;
                let fieldValue = '';
                
                if (field.type === 'checkbox' || field.type === 'radio') {
                  // Only include if checked
                  if (field.checked) {
                    shouldInclude = true;
                    fieldValue = field.value;
                  }
                } else {
                  // For text, select, textarea - include if has value
                  if (field.value) {
                    shouldInclude = true;
                    fieldValue = field.value;
                  }
                }
                
                if (shouldInclude && field.name) {
                  const propertyMatch = field.name.match(/properties\[(.*?)\]/);
                  if (propertyMatch && propertyMatch[1]) {
                    const propertyName = propertyMatch[1];
                    data.properties[propertyName] = fieldValue;
                    console.log(`Infinite Options field added to JSON: ${propertyName} = ${fieldValue}`);
                  }
                }
              });
            }
            
            options.body = JSON.stringify(data);
          } catch (e) {
            console.error('Error parsing JSON in fetch intercept:', e);
          }
        }
      } else {
        console.log('Cart operation from cart drawer - skipping ring size validation');
      }
    }
    
    // Continue with original fetch
    return originalFetch.apply(this, arguments);
  };
  
  // Form submission handler with context awareness
  function handleFormSubmission(event) {
    const form = event.target;
    
    // Check if this is a product form (not cart drawer form)
    if (form.action && form.action.includes('/cart/add')) {
      // Skip validation for cart drawer forms
      if (form.closest('.cart-drawer') || form.classList.contains('cart-drawer__form')) {
        console.log('Cart drawer form submission - skipping ring size validation');
        return;
      }
      
      console.log('Product form submission detected');
      
      const ringSizeSelect = document.getElementById('RingSize');
      
      // Only validate if we're on a product page with ring size field
      if (ringSizeSelect && !form.closest('.cart-drawer')) {
        // Validate ring size is selected
        if (!ringSizeSelect.value) {
          event.preventDefault();
          
          // Show error
          const ringSizeError = document.getElementById('ring-size-error');
          if (ringSizeError) {
            ringSizeError.style.display = 'block';
          }
          if (ringSizeSelect) {
            ringSizeSelect.style.borderColor = '#ff3860';
            ringSizeSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          console.log('Form submission blocked: Ring size not selected');
          return false;
        }
      }
    }
  }
  
  // Enhanced initialization
  function initRingSizeHandler() {
    console.log('Initializing context-aware ring size handler');
    
    // Add form submit listeners
    document.addEventListener('submit', handleFormSubmission, true);
    
    // Listen for add to cart button clicks with context checking
    document.addEventListener('click', function(event) {
      const target = event.target;
      const button = target.closest('[name="add"], .btn-product-add, .product-form__submit, button[type="submit"], input[type="submit"]');

      if (!button) {
        return;
      }

      const form = button.closest('form[data-product-form], form[data-type="add-to-cart-form"], form[action*="/cart/add"]');

      if (!form) {
        return;
      }

      // Skip if this is in the cart drawer
      if (button.closest('.cart-drawer') || button.closest('[data-cart-drawer]')) {
        console.log('Cart drawer button clicked - no ring size validation needed');
        return;
      }

      // Skip if this is a checkout button
      const buttonText = button.textContent?.toLowerCase() || '';
      const buttonValue = typeof button.value === 'string' ? button.value.toLowerCase() : '';
      if (button.name === 'checkout' || buttonText.includes('checkout') || buttonValue.includes('checkout')) {
        console.log('Checkout button clicked - no ring size validation needed');
        return;
      }

      console.log('Product add to cart button clicked');

      const ringSizeSelect = document.getElementById('RingSize');

      // Only validate if we're on a product page with ring size field
      if (ringSizeSelect && !button.closest('.cart-drawer')) {
        if (!ringSizeSelect.value) {
          event.preventDefault();
          event.stopPropagation();

          // Show error
          const ringSizeError = document.getElementById('ring-size-error');
          if (ringSizeError) {
            ringSizeError.style.display = 'block';
          }
          if (ringSizeSelect) {
            ringSizeSelect.style.borderColor = '#ff3860';
            ringSizeSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }

          console.log('Add to cart blocked: Ring size not selected');
          return false;
        }
      }
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRingSizeHandler);
  } else {
    initRingSizeHandler();
  }
  
})();
